/**
 * Which attached picture the vendor means by "this one".
 *
 * Every reference attached anywhere in a thread is sent on every turn, and for
 * a good reason: the agent often asks what to take from a picture a turn after
 * it arrived, and a list that emptied itself would leave that question
 * unanswerable. The cost is that by the third turn there are three pictures and
 * nothing distinguishing them.
 *
 * That cost came due. Asked to "create a new node inspired by the reference
 * image uploaded now", the agent used the reference from the FIRST turn — it
 * had two urls, no attachment times, and the older one came first in the array.
 * The brief it wrote described a hand holding a tile, which was the staging of
 * the old picture; the new one was a flat tile on a blue-grey wall with no hand
 * anywhere in it.
 *
 * So the list keeps its history and gains a mark: these came with the message
 * you are answering now.
 */

export interface ThreadReference {
    id: string;
    url: string;
    name?: string;
    sourceUrl?: string;
}

export interface MarkedReference extends ThreadReference {
    /**
     * Attached with THIS message.
     *
     * Absent rather than false on older ones: a field that says "no" on
     * everything reads as noise, and the agent only needs to find the yes.
     */
    attachedNow?: true;
}

/**
 * Tag the references that arrived with this turn's message.
 *
 * Matching is by id, and ids are per-attachment, so re-attaching the same
 * picture later correctly marks the new copy and not the old one.
 */
export function markLatestReferences(
    all: ThreadReference[],
    sentThisTurn: ThreadReference[],
): MarkedReference[] {
    if (sentThisTurn.length === 0) return all;
    const now = new Set(sentThisTurn.map((r) => r.id));
    return all.map((r) => (now.has(r.id) ? { ...r, attachedNow: true as const } : r));
}
